/**
 * TrackOverview Component (v2.4 - unified conditions grid)
 *
 * Single card grid. Each section (Participation, Item pool, Criteria & scales,
 * Values, Sessions) shows its own readiness state inline — amber + detail when
 * something is missing, green when done, muted when not yet reachable.
 * The whole card is the navigation target.
 *
 * No separate blocker-row panel. No forced ordering.
 */

import { useState, useEffect, useMemo, type ReactNode } from 'react';
import {
  Users,
  Package,
  ListChecks,
  BarChart3,
  Sliders,
  CheckCircle2,
  AlertTriangle,
  Circle,
  ArrowRight,
  Link2,
  Play,
  Flag,
} from 'lucide-react';
import {
  evaluateTrackReadiness,
  type TrackReadinessStatus,
} from '../../lib/trackReadinessEvaluator';
import { DecisionUnitPanel } from './DecisionUnitPanel';
import { useContextsStore } from '../../stores/prioritization/contextsStore';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { summarizeTrackRelationshipReadiness } from '../../lib/trackRelationshipReview';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import { TrackOwnerSubControl } from './TrackOwnerSubControl';

interface TrackOverviewProps {
  trackId: string;
  onNavigate: (tab: string) => void;
  refreshKey?: number;
  trackValuesConverged?: boolean;
  cycleId?: string;
  sessionCount?: number;
  completedSessionCount?: number;
}

// ─── Section definitions ──────────────────────────────────────────────────────

interface ExtraData {
  relationshipViolationCount: number;
  itemCount: number;
  rangeSetLocked: boolean;
}

interface Section {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  tab: string;
  getStatus: (r: TrackReadinessStatus, extra: ExtraData) => 'ready' | 'missing' | 'pending';
  getDetail: (r: TrackReadinessStatus, extra: ExtraData) => string | null;
}

const SECTIONS: Section[] = [
  {
    key: 'participation',
    label: 'Participation',
    description: 'Define who participates in this Track',
    icon: Users,
    tab: 'participation',
    // Optional at track level — only show green when set, neutral otherwise
    getStatus: (r) => r.s1_participation.ready ? 'ready' : 'pending',
    getDetail: (r) => r.s1_participation.ready ? null : 'No active participants',
  },
  {
    key: 'pool',
    label: 'Item pool',
    description: 'Select and lock items for prioritization',
    icon: Package,
    tab: 'pool',
    getStatus: (r) => r.s2_pool.ready ? 'ready' : 'pending',
    getDetail: (r) => r.s2_pool.ready ? null : 'No items in pool',
  },
  {
    key: 'criteria',
    label: 'Criteria',
    description: 'Select and lock the criteria for this Track',
    icon: ListChecks,
    tab: 'criteria',
    getStatus: (r) => r.s3_criteria.ready && r.s4_scales.ready ? 'ready' : 'pending',
    getDetail: (r) => r.s3_criteria.ready && r.s4_scales.ready ? null : 'No criteria defined',
  },
  {
    key: 'value-ranges',
    label: 'Value ranges',
    description: 'Set and lock value ranges for each attribute',
    icon: Sliders,
    tab: 'value-ranges',
    getStatus: (_r, extra) => extra.rangeSetLocked ? 'ready' : 'pending',
    getDetail: (_r, extra) => extra.rangeSetLocked ? null : 'Lock value ranges to confirm scales',
  },
  {
    key: 'values',
    label: 'Values',
    description: 'Enter and converge values for items',
    icon: BarChart3,
    tab: 'values',
    getStatus: (r) => r.s5_values.ready ? 'ready' : 'pending',
    getDetail: (r) => r.s5_values.ready ? null : 'No criteria defined for value entry',
  },
  {
    key: 'relationships',
    label: 'Relationships',
    description: 'Review relationship constraints across pool items',
    icon: Link2,
    tab: 'relationships',
    // Only amber when there are actual violations — that's a real problem worth flagging
    getStatus: (_r, extra) => {
      if (extra.itemCount === 0) return 'pending';
      return extra.relationshipViolationCount === 0 ? 'ready' : 'missing';
    },
    getDetail: (_r, extra) => {
      if (extra.itemCount === 0) return null;
      return extra.relationshipViolationCount > 0
        ? `${extra.relationshipViolationCount} scope violation${extra.relationshipViolationCount !== 1 ? 's' : ''} need resolution`
        : null;
    },
  },
];

// ─── Card ─────────────────────────────────────────────────────────────────────

function SectionCard({
  section,
  readiness,
  extra,
  onClick,
  footer,
}: {
  section: Section;
  readiness: TrackReadinessStatus;
  extra: ExtraData;
  onClick: () => void;
  footer?: ReactNode;
}) {
  const status = section.getStatus(readiness, extra);
  const detail = section.getDetail(readiness, extra);
  const Icon = section.icon;

  const cardClass =
    status === 'ready'
      ? 'border-[var(--green)] bg-[var(--green-dim)]/95 hover:border-[var(--green)] hover:bg-[var(--green-dim)] dark:border-[var(--green)]/40 dark:bg-[var(--green)]/20 dark:hover:border-[var(--green)]/60'
      : status === 'missing'
        ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]/95 hover:border-[var(--mustard)] hover:bg-[var(--mustard-dim)] dark:border-[var(--mustard)]/50 dark:bg-[var(--mustard)]/20 dark:hover:border-[var(--mustard)]/70'
        : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] hover:bg-[var(--surface)]/90 dark:border-[var(--rule)]/50 dark:bg-[var(--bg-raised)]/20 dark:hover:border-[var(--rule)]/70';

  const iconClass =
    status === 'ready'
      ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
      : status === 'missing'
        ? 'text-[var(--mustard)] dark:text-[var(--mustard)]'
        : 'text-[var(--text-ter)]';

  const labelClass =
    status === 'ready'
      ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
      : status === 'missing'
        ? 'text-[var(--mustard)] dark:text-[var(--mustard)]'
        : 'text-[var(--text-pri)]';

  const statusIcon =
    status === 'ready' ? (
      <CheckCircle2 className="h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)] shrink-0" />
    ) : status === 'missing' ? (
      <AlertTriangle className="h-4 w-4 text-[var(--mustard)] dark:text-[var(--mustard)] shrink-0" />
    ) : (
      <Circle className="h-4 w-4 text-[var(--text-ter)] shrink-0" />
    );

  return (
    <div className={` border transition-colors ${cardClass} ${footer ? 'overflow-hidden' : ''}`}>
      <button
        type="button"
        onClick={onClick}
        className={`group text-left w-full px-4 py-4 ${footer ? ' rounded-b-none' : ''}`}
      >
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2">
            <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} />
            <span className={`text-sm font-semibold ${labelClass}`}>{section.label}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {statusIcon}
            <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-sec)] dark:group-hover:text-[var(--text-ter)] transition-colors" />
          </div>
        </div>
        <p className="text-xs text-[var(--text-sec)]">{section.description}</p>
        {detail && status !== 'ready' && (
          <p
            className={`text-xs mt-1.5 ${
              status === 'missing' ? 'text-[var(--mustard)] dark:text-[var(--mustard)]' : 'text-[var(--text-sec)]'
            }`}
          >
            {detail}
          </p>
        )}
      </button>
      {footer && (
        <div
          className="px-3 pb-3 pt-0 border-t border-[var(--rule)] dark:border-white/10"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function TrackOverview({ trackId, onNavigate, refreshKey, cycleId: cycleIdProp, sessionCount = 0, completedSessionCount = 0 }: TrackOverviewProps) {
  const [readiness, setReadiness] = useState<TrackReadinessStatus | null>(null);
  const [resolvedCycleId, setResolvedCycleId] = useState<string | null>(cycleIdProp ?? null);
  const [trackContextId, setTrackContextId] = useState<string | null>(null);
  const [rangeSetLocked, setRangeSetLocked] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchContextForCycle = useContextsStore((s) => s.fetchContextForCycle);

  // Relationship violation count + item count — computed from the live store
  const allPoolsRaw = useItemPoolsStore((s) => s.itemPools);
  const groupsRaw = useItemRelationshipsStore((s) => s.groups);
  const membershipsRaw = useItemRelationshipsStore((s) => s.groupMemberships);
  const edgesRaw = useItemRelationshipsStore((s) => s.edges);
  // Memoize the `|| []` fallbacks so the deps of the useMemo below stay referentially stable.
  const allPools = useMemo(() => allPoolsRaw || [], [allPoolsRaw]);
  const groups = useMemo(() => groupsRaw || [], [groupsRaw]);
  const memberships = useMemo(() => membershipsRaw || [], [membershipsRaw]);
  const edges = useMemo(() => edgesRaw || [], [edgesRaw]);

  const { itemCount, relationshipViolationCount } = useMemo(() => {
    const trackPool = allPools.find((p) => p.trackId === trackId);
    const poolItemIds: string[] = trackPool?.priorityIds ?? [];
    const poolItems = poolItemIds.map((id) => ({ id }));
    const summary = summarizeTrackRelationshipReadiness(poolItems, groups, memberships, edges);
    return { itemCount: poolItemIds.length, relationshipViolationCount: summary.itemsNeedingReview };
  }, [allPools, trackId, groups, memberships, edges]);

  const extra: ExtraData = { relationshipViolationCount, itemCount, rangeSetLocked };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    evaluateTrackReadiness(trackId).then((r) => {
      if (!cancelled) { setReadiness(r ?? null); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [trackId, refreshKey]);

  // Fetch range_set_locked_at for the value ranges readiness card
  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !trackId) return;
    let cancelled = false;
    supabase
      .from('tracks')
      .select('range_set_locked_at')
      .eq('id', trackId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setRangeSetLocked(!!(data as { range_set_locked_at?: string | null } | null)?.range_set_locked_at);
      });
    return () => { cancelled = true; };
  }, [trackId, refreshKey]);

  useEffect(() => {
    if (cycleIdProp) { setResolvedCycleId(cycleIdProp); return; }
    if (readiness?.cycleId) { setResolvedCycleId(readiness.cycleId); return; }
    const supabase = getSafeSupabaseClient();
    if (!supabase || !trackId) return;
    let cancelled = false;
    supabase
      .from('tracks').select('cycle_id').eq('id', trackId).maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data?.cycle_id) setResolvedCycleId(data.cycle_id as string);
      });
    return () => { cancelled = true; };
  }, [trackId, cycleIdProp, readiness?.cycleId]);

  useEffect(() => {
    if (!resolvedCycleId) return;
    let cancelled = false;
    fetchContextForCycle(resolvedCycleId).then((ctx) => {
      if (!cancelled) setTrackContextId(ctx?.id ?? null);
    });
    return () => { cancelled = true; };
  }, [resolvedCycleId, fetchContextForCycle]);

  if (loading || !readiness) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {SECTIONS.map((s) => (
          <div
            key={s.key}
            className="border border-[var(--rule)] bg-[var(--hover-tint)]/80 dark:border-[var(--rule)]/50 dark:bg-[var(--bg-raised)]/20 px-4 py-4 animate-pulse h-20"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Decision Unit — accumulated evidence across sessions and loops */}
      {trackContextId && (
        <DecisionUnitPanel
          contextId={trackContextId}
          trackId={trackId}
          activeCycleId={resolvedCycleId ?? undefined}
          compact
        />
      )}

      {/* Readiness grid — six setup areas (distinct from run/output below) */}
      <div>
        {readiness.serverError && (
          <p role="status" className="mb-2 text-xs text-[var(--mustard)]">
            The server readiness check failed ({readiness.serverError}). Showing an estimate worked out in your browser — it may differ from what the server enforces.
          </p>
        )}
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2">
          Track setup
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SECTIONS.map((section) => (
            <SectionCard
              key={section.key}
              section={section}
              readiness={readiness}
              extra={extra}
              onClick={() => onNavigate(section.tab)}
              footer={
                section.key === 'participation' ? (
                  <TrackOwnerSubControl trackId={trackId} variant="light" />
                ) : undefined
              }
            />
          ))}
        </div>
      </div>

      {/* Sessions & results — separate band: horizontal strip cards, not readiness tiles */}
      <div className="border border-[var(--slate)]/90 bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4 shadow-sm dark:border-[var(--slate)]/20 dark:from-slate-950/90 dark:via-slate-900/60 dark:to-indigo-950/50 dark:shadow-inner dark:shadow-black/20">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]/75 mb-3 dark:text-[var(--text-ter)]/80">
          Run & review
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => onNavigate('sessions')}
            className="group flex w-full text-left border border-[var(--slate)] bg-[var(--bg-raised)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/80 transition-colors overflow-hidden shadow-sm dark:border-[var(--slate)]/25 dark:bg-[var(--slate)]/30 dark:hover:bg-[var(--slate)]/45 dark:hover:border-[var(--slate)]/40 dark:shadow-none"
          >
            <div className="w-1 shrink-0 bg-[var(--slate)] group-hover:bg-[var(--slate)] dark:bg-[var(--slate)]/70 dark:group-hover:bg-[var(--slate)]" aria-hidden />
            <div className="flex-1 min-w-0 px-4 py-3.5">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center bg-[var(--slate-dim)] border border-[var(--slate)] dark:bg-[var(--slate)]/15 dark:border-[var(--slate)]/30">
                    <Play className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                  </div>
                  <span className="text-sm font-semibold text-[var(--text-pri)] truncate dark:text-[var(--text-ter)]">Sessions</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {sessionCount > 0 && (
                    <span className="text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] px-2 py-0.5 rounded-full tabular-nums dark:bg-[var(--slate)]/35">
                      {sessionCount}
                    </span>
                  )}
                  <ArrowRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] dark:text-[var(--text-ter)]/70 dark:group-hover:text-[var(--text-ter)] transition-colors" />
                </div>
              </div>
              <p className="text-xs text-[var(--text-sec)] pl-10 dark:text-[var(--text-ter)]/70">
                {sessionCount === 0
                  ? 'No sessions yet — run prioritization sessions for this Track'
                  : `${sessionCount} session${sessionCount !== 1 ? 's' : ''} linked to this Track`}
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => onNavigate('results-panel')}
            className={`group flex w-full text-left  border overflow-hidden transition-colors shadow-sm dark:shadow-none ${
              completedSessionCount > 0
                ? 'border-[var(--slate)] bg-[var(--slate-dim)]/90 hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] dark:border-[var(--slate)]/35 dark:bg-[var(--slate)]/35 dark:hover:border-[var(--slate)]/50 dark:hover:bg-[var(--slate)]/50'
                : 'border-[var(--slate)]/80 bg-[var(--bg-raised)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)]/60 dark:border-[var(--slate)]/20 dark:bg-[var(--slate)]/20 dark:hover:border-[var(--slate)]/35 dark:hover:bg-[var(--slate)]/30'
            }`}
          >
            <div
              className={`w-1 shrink-0 ${completedSessionCount > 0 ? 'bg-[var(--slate)] group-hover:bg-[var(--slate)]' : 'bg-[var(--slate)] group-hover:bg-[var(--slate)] dark:bg-[var(--slate)]/50 dark:group-hover:bg-[var(--slate)]/80'}`}
              aria-hidden
            />
            <div className="flex-1 min-w-0 px-4 py-3.5">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center  border ${
                      completedSessionCount > 0
                        ? 'bg-[var(--slate-dim)] border-[var(--slate)] dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]/40'
                        : 'bg-[var(--slate-dim)] border-[var(--slate)]/90 dark:bg-[var(--slate)]/10 dark:border-[var(--slate)]/25'
                    }`}
                  >
                    <Flag
                      className={`h-4 w-4 ${completedSessionCount > 0 ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]/70'}`}
                    />
                  </div>
                  <span
                    className={`text-sm font-semibold truncate ${completedSessionCount > 0 ? 'text-[var(--text-pri)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)] dark:text-[var(--text-ter)]/90'}`}
                  >
                    Results
                  </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {completedSessionCount > 0 && (
                    <span className="text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] px-2 py-0.5 rounded-full tabular-nums dark:bg-[var(--slate)]/40">
                      {completedSessionCount}
                    </span>
                  )}
                  <ArrowRight className="h-4 w-4 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] dark:text-[var(--text-ter)]/70 dark:group-hover:text-[var(--text-ter)] transition-colors" />
                </div>
              </div>
              <p className="text-xs text-[var(--text-sec)] pl-10 dark:text-[var(--text-ter)]/70">
                {completedSessionCount === 0
                  ? 'No completed sessions yet — results appear after sessions are published'
                  : `${completedSessionCount} completed session${completedSessionCount !== 1 ? 's' : ''} ready to review`}
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

export default TrackOverview;
